import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Manifesto from "@/components/Manifesto";
import Sectors from "@/components/Sectors";
import Marquee from "@/components/Marquee";
import Edge from "@/components/Edge";
import ContactSection from "@/components/ContactSection";
import ContactModal from "@/components/ContactModal";
import Footer from "@/components/Footer";
import Insights from "@/pages/Insights";
import Bureau from "@/pages/Bureau";
import { initLenis, scrollToSection } from "@/lib/smooth";

const BootScreen = () => (
  <motion.div
    exit={{ y: "-100%" }}
    transition={{ duration: 0.85, ease: [0.76, 0, 0.24, 1] }}
    className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#0B0C10]"
    data-testid="boot-screen"
  >
    <motion.img
      src="/logo.png"
      alt="Future Factory Group logo"
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="h-20 w-auto"
    />
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.25 }}
      className="mt-8 font-mono text-[10px] uppercase tracking-[0.5em] text-white/50"
    >
      Initializing Future Factory<span className="cursor-blink text-cyber">_</span>
    </motion.p>
  </motion.div>
);

const ScrollReset = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname !== "/") scrollToSection(0);
  }, [pathname]);
  return null;
};

const Home = ({ started, onContact }) => {
  const location = useLocation();
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      const t = setTimeout(() => scrollToSection(target), 500);
      return () => clearTimeout(t);
    }
  }, [location.state]);

  return (
    <main>
      <Hero started={started} onContact={() => onContact()} />
      <Manifesto />
      <Sectors onContact={onContact} />
      <Marquee />
      <Edge />
      <ContactSection onContact={() => onContact()} />
    </main>
  );
};

function App() {
  const [booted, setBooted] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactType, setContactType] = useState(null);

  useEffect(() => {
    initLenis();
    const t = setTimeout(() => setBooted(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const openContact = (type) => {
    setContactType(type || null);
    setContactOpen(true);
  };

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-abyss text-white">
        <AnimatePresence>{!booted && <BootScreen />}</AnimatePresence>
        <div className="noise-layer" />
        <Toaster theme="dark" position="bottom-right" />
        <ScrollReset />
        <Navbar onContact={() => openContact()} />
        <Routes>
          <Route path="/" element={<Home started={booted} onContact={openContact} />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/bureau" element={<Bureau />} />
        </Routes>
        <Footer />
        <ContactModal
          open={contactOpen}
          onClose={() => setContactOpen(false)}
          defaultType={contactType}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
